   <template>
    <div class="flex-1 overflow-x-hidden overflow-y-auto p-2 sm:p-4 pr-2 sm:pr-8 max-w-full">
      <!-- Back Navigation Header -->
      <div class="mb-6">
        <button 
          @click="goBackToCampaigns" 
          class="inline-flex items-center cursor-pointer px-4 py-2 text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 back-to-campaigns-btn"
        >
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
          </svg>
          Back to Email campaigns
        </button>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading || campaignProcessing" class="flex justify-center items-center h-64">
        <div class="text-center">
          <span class="custom-spinner mx-auto mb-4"></span>
          <p v-if="campaignProcessing" class="text-gray-600">Setting up your campaign dashboard...</p>
          <p v-else class="text-gray-600">Loading campaign data...</p>
        </div>
      </div>

      <!-- Error State -->
      <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">Error loading campaign data</h3>
            <div class="mt-2 text-sm text-red-700">{{ error }}</div>
          </div>
        </div>
      </div>

      <!-- Campaign Status State -->
      <div v-if="campaignState && !campaignInProgress && !isLoading" class="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6 max-w-full overflow-visible">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg v-if="isCampaignScheduled" class="h-8 w-8 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <svg v-else-if="isCampaignPendingSchedule" class="h-8 w-8 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <svg v-else class="h-8 w-8 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <div class="ml-4">
            <h3 class="text-lg font-medium text-yellow-800">{{ campaignStatusMessage }}</h3>
            <div class="mt-2 text-sm text-yellow-700">
              <p>{{ campaignStatusDescription }}</p>
              <p v-if="isCampaignScheduled" class="mt-2 font-medium">The campaign will automatically start at the scheduled time.</p>
              <p v-else-if="isCampaignPendingSchedule" class="mt-2 font-medium">Please schedule the campaign to begin sending messages.</p>
            </div>
            <div class="mt-4">
              <button 
                @click="fetchReport" 
                class="inline-flex items-center cursor-pointer px-4 py-2 text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors duration-200 refresh-status-btn"
              >
                <svg :class="{ 'animate-spin': isLoading }" class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                </svg>
                Refresh Status
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Campaign In Progress State -->
      <div v-if="campaignInProgress && !isLoading" class="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-8 w-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <div class="ml-4">
            <h3 class="text-lg font-medium text-blue-800">Campaign in Progress</h3>
            <div class="mt-2 text-sm text-blue-700">
              <p>{{ campaignStatusDescription }}</p>
              <p class="mt-2 font-medium">Please check back in a few minutes for detailed statistics.</p>
            </div>
            <div class="mt-4">
              <button 
                @click="fetchReport" 
                class="inline-flex items-center cursor-pointer px-4 py-2 text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 refresh-report-btn"
              >
                <svg :class="{ 'animate-spin': isLoading }" class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                </svg>
                Refresh Report
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Template Deletion Error Alert -->
      <div v-if="hasTemplateErrors" class="bg-red-50 border border-red-300 rounded-lg p-6 mb-6">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-8 w-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
            </svg>
          </div>
          <div class="ml-4 flex-1">
            <h3 class="text-lg font-medium text-red-800">Email template(s) missing</h3>
            <div class="mt-2 text-sm text-red-700">
              <p v-if="macro">
                The macro "<span class="font-semibold">{{ macro.name }}</span>" is using the following template(s) that are missing or were deleted:
              </p>
              <p v-else>{{ templateDeletionErrorMessage }}</p>
            </div>
            <div v-if="deletedTemplates.length > 0" class="mt-3">
              <ul class="list-disc list-inside text-sm text-red-700 space-y-1">
                <li v-for="template in deletedTemplates" :key="template.template_id">
                  <span class="font-semibold">{{ template.template_name }}</span>
                  <span class="text-red-500 text-xs ml-2">(Deleted: {{ formatEventTime(template.deleted_at) }})</span>
                </li>
              </ul>
            </div>
            <div class="mt-3 text-sm text-red-600">
              <p class="font-medium">Impact: Messages using these templates were skipped during campaign execution.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Campaign Info for All Campaign States -->
      <div v-if="campaign && (campaignState || campaignInProgress)" class="bg-white dark:bg-slate-800 rounded-lg shadow-sm border p-6 border-slate-200 dark:border-slate-700">
        <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">
          Campaign Info
        </h3>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Display ID</label>
            <p class="mt-1 text-slate-800 dark:text-slate-100">{{ campaign.display_id }}</p>
          </div>
          <div>
            <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Title</label>
            <p class="mt-1 text-slate-800 dark:text-slate-100">{{ campaign.title }}</p>
          </div>
          <div>
            <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Type</label>
            <p class="mt-1 text-slate-800 dark:text-slate-100">{{ campaign.campaign_type }}</p>
          </div>
          <div>
            <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Status</label>
            <span 
              class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
              :class="completionStatusClass"
            >
              {{ campaign.campaign_status || 'In Progress' }}
            </span>
          </div>
        </div>
      </div>

      <!-- Campaign Info for Completed Campaigns -->
      <div v-if="campaign && !campaignInProgress && !campaignState" class="bg-white dark:bg-slate-800 rounded-lg shadow-sm border p-6 border-slate-200 dark:border-slate-700">
        <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">
          Campaign Info
        </h3>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Display ID</label>
            <p class="mt-1 text-slate-800 dark:text-slate-100">{{ campaign.display_id }}</p>
          </div>
          <div>
            <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Title</label>
            <p class="mt-1 text-slate-800 dark:text-slate-100">{{ campaign.title }}</p>
          </div>
          <div>
            <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Type</label>
            <p class="mt-1 text-slate-800 dark:text-slate-100">{{ campaign.campaign_type }}</p>
          </div>
          <div>
            <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Completion Status</label>
            <span
              class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
              :class="completionStatusClass"
            >
              {{ campaign.completion_status || 'N/A' }}
            </span>
          </div>
        </div>
      </div>
  
      <!-- Campaign Details -->
      <div v-if="campaign" class="bg-white dark:bg-slate-800 rounded-lg shadow-sm border p-6 mt-6 border-slate-200 dark:border-slate-700">
        <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">Campaign Details</h3>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Status</label>
            <span
              class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
              :class="statusClass"
            >
              {{ effectiveStatus }}
            </span>
          </div>
          <div>
            <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Macro</label>
            <p class="mt-1 text-slate-800 dark:text-slate-100">{{ macro?.name || 'N/A' }}</p>
          </div>
          <div>
            <label class="text-sm font-medium text-slate-600 dark:text-slate-300">{{ campaignDurationLabel }}</label>
            <p class="mt-1 text-slate-800 dark:text-slate-100">{{ formattedCampaignDuration }}</p>
          </div>
          <div v-if="campaign.email_outbound_provider_label">
            <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Email provider</label>
            <p class="mt-1 text-slate-800 dark:text-slate-100">{{ campaign.email_outbound_provider_label }}</p>
          </div>
        </div>
      </div>
  
      <!-- Schedule -->
      <div v-if="campaign" class="bg-white dark:bg-slate-800 rounded-lg shadow-sm border p-6 mt-6 border-slate-200 dark:border-slate-700">
        <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">Schedule</h3>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Created At</label>
            <p class="mt-1 text-slate-800 dark:text-slate-100">{{ formattedCreatedAt }}</p>
          </div>
          <div>
            <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Scheduled At</label>
            <p class="mt-1 text-slate-800 dark:text-slate-100">{{ formattedScheduledAt }}</p>
          </div>
        </div>
      </div>

      <!-- Audience -->
      <div v-if="showAudienceSection" class="bg-white dark:bg-slate-800 rounded-lg shadow-sm border p-6 mt-6 border-slate-200 dark:border-slate-700">
        <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">Audience</h3>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label class="text-sm font-medium text-slate-600 dark:text-slate-300">{{ audienceTypeLabel }}</label>
            <ul v-if="showAudienceLabels" class="list-none list-inside mt-2">
              <li v-for="label in audienceLabels" :key="label.id" class="flex items-center mb-2 text-slate-800 dark:text-slate-100">
                <span class="w-4 h-4 rounded-md inline-block mr-2" :style="{ backgroundColor: label.color }"></span>
                {{ label.title }}
              </li>
            </ul>
            <p v-else-if="audienceDisplayName" class="mt-2 text-slate-800 dark:text-slate-100">
              {{ audienceDisplayName }}
            </p>
            <p v-else class="mt-2 text-slate-500 dark:text-slate-400">N/A</p>
          </div>
          <div v-if="engagement">
            <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Engagement Metrics</label>
            <div class="mt-2 space-y-2">
              <div class="flex justify-between items-center">
                <div class="flex items-center">
                  <span class="text-sm text-slate-600 dark:text-slate-300">Total Audience:</span>
                  <div class="ml-1 group relative flex items-center justify-center">
                    <svg class="w-3 h-3 text-gray-500 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"></path>
                    </svg>
                    <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-3 py-2 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-auto z-[9999] w-48 sm:w-64 max-w-xs shadow-lg border border-gray-600 backdrop-blur-sm" style="background-color: #000000; color: #ffffff;">
                      Total number of contacts targeted in this campaign
                    </div>
                  </div>
                </div>
                <span class="font-medium text-slate-800 dark:text-slate-100">{{ engagement.total_audience || 0 }}</span>
              </div>
              <div class="flex justify-between items-center">
                <div class="flex items-center">
                  <span class="text-sm text-slate-600 dark:text-slate-300">Engaged Contacts:</span>
                  <div class="ml-1 group relative flex items-center justify-center">
                    <svg class="w-3 h-3 text-gray-500 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"></path>
                    </svg>
                    <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-3 py-2 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-auto z-[9999] w-48 sm:w-64 max-w-xs shadow-lg border border-gray-600 backdrop-blur-sm" style="background-color: #000000; color: #ffffff;">
                      Number of contacts who actually received campaign messages
                    </div>
                  </div>
                </div>
                <span class="font-medium text-slate-800 dark:text-slate-100">{{ engagement.engaged_contacts || 0 }}</span>
              </div>
              <div class="flex justify-between items-center">
                <div class="flex items-center">
                  <span class="text-sm text-slate-600 dark:text-slate-300">Engagement Rate:</span>
                  <div class="ml-1 group relative flex items-center justify-center">
                    <svg class="w-3 h-3 text-gray-500 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"></path>
                    </svg>
                    <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-3 py-2 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-auto z-[9999] w-48 sm:w-64 max-w-xs shadow-lg border border-gray-600 backdrop-blur-sm" style="background-color: #000000; color: #ffffff;">
                      Percentage of targeted contacts who received campaign messages
                    </div>
                  </div>
                </div>
                <span class="font-medium text-slate-800 dark:text-slate-100">{{ engagement.engagement_rate || 0 }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
  
      <!-- Enhanced Statistics Cards (SendGrid campaigns: delivery/read/fail counts from category stats) -->
      <div v-if="mergedStats" class="mt-6 max-w-full overflow-visible">
        <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-2">Performance Metrics</h3>
        <p
          v-if="campaign && campaign.email_outbound_provider === 'sendgrid' && sendgridStats && !sendgridStats.error"
          class="text-sm text-slate-500 dark:text-slate-400 mb-4"
        >
          Metrics from SendGrid for category
          <span class="font-mono text-xs">{{ sendgridStats.category }}</span>.
          <span v-if="sendgridStats.note === 'no_category_activity_yet'" class="block mt-2 text-amber-800 dark:text-amber-200">
            SendGrid has not recorded any mail for this category yet (no tagged sends, or only older sends before categories were added). Values stay at zero until new campaign email goes out through SendGrid with this category.
          </span>
          <span v-else class="block mt-1">
            Only messages sent with category tagging include this label; older sends may show zeros.
          </span>
        </p>
        <div
          v-else-if="campaign && campaign.email_outbound_provider === 'sendgrid' && sendgridStats && sendgridStats.error"
          class="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-4"
        >
          <p class="text-sm text-amber-900 dark:text-amber-100">
            SendGrid statistics could not be loaded<span v-if="sendgridStats.error"> ({{ sendgridStats.error }})</span>.
            Showing Chatwoot message metrics below.
            <span v-if="sendgridStats.message" class="block mt-1">{{ formatSendgridErrorMessage(sendgridStats.message) }}</span>
          </p>
        </div>

        <!-- Primary Metrics -->
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          <div class="bg-green-50 p-3 sm:p-4 rounded-lg text-center border border-slate-200 dark:border-slate-700 relative">
            <div class="flex items-center justify-center mb-1">
              <p class="text-sm font-medium text-green-700">Delivery Rate</p>
              <div class="ml-1 group relative flex items-center justify-center">
                <svg class="w-4 h-4 text-green-600 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"></path>
                </svg>
                <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-3 py-2 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-auto z-[9999] w-48 sm:w-64 max-w-xs shadow-lg border border-gray-600 backdrop-blur-sm" style="background-color: #000000; color: #ffffff;">
                  Percentage of messages successfully delivered to recipients out of total messages sent
                </div>
              </div>
            </div>
            <p class="text-xl sm:text-2xl font-bold">{{ mergedStats.delivery_rate || 0 }}%</p>
          </div>
          
          <div class="bg-blue-50 p-3 sm:p-4 rounded-lg text-center border border-slate-200 dark:border-slate-700 relative">
            <div class="flex items-center justify-center mb-1">
              <p class="text-sm font-medium text-blue-700">Read Rate</p>
              <div class="ml-1 group relative flex items-center justify-center">
                <svg class="w-4 h-4 text-blue-600 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"></path>
                </svg>
                <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-3 py-2 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-auto z-[9999] w-48 sm:w-64 max-w-xs shadow-lg border border-gray-600 backdrop-blur-sm" style="background-color: #000000; color: #ffffff;">
                  Percentage of delivered messages that were actually read by recipients
                </div>
              </div>
            </div>
            <p class="text-xl sm:text-2xl font-bold">{{ mergedStats.read_rate || 0 }}%</p>
          </div>
          
          <div class="bg-purple-50 p-3 sm:p-4 rounded-lg text-center border border-slate-200 dark:border-slate-700 relative">
            <div class="flex items-center justify-center mb-1">
              <p class="text-sm font-medium text-purple-700">Engagement Score</p>
              <div class="ml-1 group relative flex items-center justify-center">
                <svg class="w-4 h-4 text-purple-600 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"></path>
                </svg>
                <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-3 py-2 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-auto z-[9999] w-48 sm:w-64 max-w-xs shadow-lg border border-gray-600 backdrop-blur-sm" style="background-color: #000000; color: #ffffff;">
                  Combined score measuring overall campaign effectiveness based on delivery and read rates
                </div>
              </div>
            </div>
            <p class="text-xl sm:text-2xl font-bold">{{ mergedStats.engagement_score || 0 }}</p>
          </div>
          
          <div class="bg-orange-50 p-3 sm:p-4 rounded-lg text-center border border-slate-200 dark:border-slate-700 relative">
            <div class="flex items-center justify-center mb-1">
              <p class="text-sm font-medium text-orange-700">Reply Rate</p>
              <div class="ml-1 group relative flex items-center justify-center">
                <svg class="w-4 h-4 text-orange-600 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"></path>
                </svg>
                    <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-3 py-2 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-auto z-[9999] w-48 sm:w-64 max-w-xs shadow-lg border border-gray-600 backdrop-blur-sm" style="background-color: #000000; color: #ffffff;">
                      Percentage of unique recipients who replied directly to campaign messages (before any agent follow-up)
                    </div>
              </div>
            </div>
            <p class="text-xl sm:text-2xl font-bold">{{ mergedStats.reply_rate || 0 }}%</p>
          </div>
          
          <div class="bg-red-50 p-3 sm:p-4 rounded-lg text-center border border-slate-200 dark:border-slate-700 relative">
            <div class="flex items-center justify-center mb-1">
              <p class="text-sm font-medium text-red-700">Failed</p>
              <div class="ml-1 group relative flex items-center justify-center">
                <svg class="w-4 h-4 text-red-600 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"></path>
                </svg>
                <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-3 py-2 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-auto z-[9999] w-48 sm:w-64 max-w-xs shadow-lg border border-gray-600 backdrop-blur-sm" style="background-color: #000000; color: #ffffff;">
                  Number of messages that could not be delivered due to errors or issues
                </div>
              </div>
            </div>
            <p class="text-xl sm:text-2xl font-bold">{{ mergedStats.failed || 0 }}</p>
          </div>
        </div>

        <!-- Secondary Metrics -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <div class="bg-yellow-50 p-3 sm:p-4 rounded-lg text-center border border-slate-200 dark:border-slate-700 relative">
            <div class="flex items-center justify-center mb-1">
              <p class="text-sm font-medium text-yellow-700">Avg Delivery Time</p>
              <div class="ml-1 group relative flex items-center justify-center">
                <svg class="w-4 h-4 text-yellow-600 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"></path>
                </svg>
                <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-3 py-2 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-auto z-[9999] w-48 sm:w-64 max-w-xs shadow-lg border border-gray-600 backdrop-blur-sm" style="background-color: #000000; color: #ffffff;">
                  Average time taken for messages to be delivered to recipients after sending
                </div>
              </div>
            </div>
            <p class="text-lg font-bold">{{ mergedStats.avg_delivery_time || 0 }}m</p>
          </div>
          
          <div class="bg-indigo-50 p-3 sm:p-4 rounded-lg text-center border border-slate-200 dark:border-slate-700 relative">
            <div class="flex items-center justify-center mb-1">
              <p class="text-sm font-medium text-indigo-700">Unique Recipients</p>
              <div class="ml-1 group relative flex items-center justify-center">
                <svg class="w-4 h-4 text-indigo-600 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"></path>
                </svg>
                <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-3 py-2 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-auto z-[9999] w-48 sm:w-64 max-w-xs shadow-lg border border-gray-600 backdrop-blur-sm" style="background-color: #000000; color: #ffffff;">
                  Number of unique contacts who received campaign messages
                </div>
              </div>
            </div>
            <p class="text-xl sm:text-2xl font-bold">{{ mergedStats.unique_recipients || 0 }}</p>
          </div>
          
          <div class="bg-orange-50 p-3 sm:p-4 rounded-lg text-center border border-slate-200 dark:border-slate-700 relative">
            <div class="flex items-center justify-center mb-1">
              <p class="text-sm font-medium text-orange-700">Avg Read Time</p>
              <div class="ml-1 group relative flex items-center justify-center">
                <svg class="w-4 h-4 text-orange-600 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"></path>
                </svg>
                <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-3 py-2 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-auto z-[9999] w-48 sm:w-64 max-w-xs shadow-lg border border-gray-600 backdrop-blur-sm" style="background-color: #000000; color: #ffffff;">
                  Average time taken for recipients to read messages after delivery
                </div>
              </div>
            </div>
            <p class="text-lg font-bold">{{ mergedStats.avg_read_time || 0 }}m</p>
          </div>
        </div>

        <!-- Message Status Breakdown -->
        <div class="bg-white dark:bg-slate-800 rounded-lg shadow-sm border p-6 border-slate-200 dark:border-slate-700 max-w-full overflow-visible">
          <h4 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">Message Status Breakdown</h4>
          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div class="text-center relative">
              <div class="flex items-center justify-center mb-1">
                <p class="text-sm font-medium text-gray-600 dark:text-slate-300">Total Messages</p>
                <div class="ml-1 group relative flex items-center justify-center">
                  <svg class="w-4 h-4 text-gray-500 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"></path>
                  </svg>
                  <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-3 py-2 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-auto z-[9999] w-48 sm:w-64 max-w-xs shadow-lg border border-gray-600 backdrop-blur-sm" style="background-color: #000000; color: #ffffff;">
                    Total number of messages sent in this campaign
                  </div>
                </div>
              </div>
              <p class="text-xl sm:text-2xl font-bold text-gray-900 dark:text-slate-100">{{ mergedStats.total_messages || 0 }}</p>
            </div>
            <div class="text-center relative">
              <div class="flex items-center justify-center mb-1">
                <p class="text-sm font-medium text-blue-600">Sent</p>
                <div class="ml-1 group relative flex items-center justify-center">
                  <svg class="w-4 h-4 text-blue-500 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"></path>
                  </svg>
                  <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-3 py-2 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-auto z-[9999] w-48 sm:w-64 max-w-xs shadow-lg border border-gray-600 backdrop-blur-sm" style="background-color: #000000; color: #ffffff;">
                    Messages sent but not yet delivered to recipients
                  </div>
                </div>
              </div>
              <p class="text-xl sm:text-2xl font-bold text-blue-900 dark:text-blue-300">{{ mergedStats.sent || 0 }}</p>
            </div>
            <div class="text-center relative">
              <div class="flex items-center justify-center mb-1">
                <p class="text-sm font-medium text-green-600">Delivered</p>
                <div class="ml-1 group relative flex items-center justify-center">
                  <svg class="w-4 h-4 text-green-500 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"></path>
                  </svg>
                  <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-3 py-2 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-auto z-[9999] w-48 sm:w-64 max-w-xs shadow-lg border border-gray-600 backdrop-blur-sm" style="background-color: #000000; color: #ffffff;">
                    Messages successfully delivered to recipient devices
                  </div>
                </div>
              </div>
              <p class="text-xl sm:text-2xl font-bold text-green-900 dark:text-green-300">{{ mergedStats.delivered || 0 }}</p>
            </div>
            <div class="text-center relative">
              <div class="flex items-center justify-center mb-1">
                <p class="text-sm font-medium text-purple-600">Read</p>
                <div class="ml-1 group relative flex items-center justify-center">
                  <svg class="w-4 h-4 text-purple-500 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"></path>
                  </svg>
                  <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-3 py-2 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-auto z-[9999] w-48 sm:w-64 max-w-xs shadow-lg border border-gray-600 backdrop-blur-sm" style="background-color: #000000; color: #ffffff;">
                    Messages that have been opened and read by recipients
                  </div>
                </div>
              </div>
              <p class="text-xl sm:text-2xl font-bold text-purple-900 dark:text-purple-300">{{ mergedStats.read || 0 }}</p>
            </div>
            <div class="text-center relative">
              <div class="flex items-center justify-center mb-1">
                <p class="text-sm font-medium text-orange-600">Replies</p>
                <div class="ml-1 relative flex items-center justify-center">
                  <svg 
                    class="w-4 h-4 text-orange-500 cursor-help" 
                    fill="currentColor" 
                    viewBox="0 0 20 20"
                    @mouseenter="showRepliesTooltip = true"
                    @mouseleave="hideRepliesTooltip"
                  >
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"></path>
                  </svg>
                  <div 
                    v-show="showRepliesTooltip"
                    class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-3 py-2 text-xs rounded-lg transition-opacity duration-200 z-[9999] w-48 sm:w-64 max-w-xs shadow-lg border border-gray-600 backdrop-blur-sm" 
                    style="background-color: #000000; color: #ffffff;"
                    @mouseenter="showRepliesTooltip = true"
                    @mouseleave="hideRepliesTooltip"
                  >
                    Number of unique contacts who replied directly to campaign messages (before any agent follow-up)
                  </div>
                </div>
              </div>
              <p class="text-xl sm:text-2xl font-bold text-orange-900 dark:text-orange-300">{{ mergedStats.replies || 0 }}</p>
            </div>
          </div>
        </div>

        <!-- Error Analysis -->
        <div v-if="stats.error_breakdown && (stats.error_breakdown.error_types && Object.keys(stats.error_breakdown.error_types).length > 0)" class="bg-white dark:bg-slate-800 rounded-lg shadow-sm border p-6 mt-6 border-slate-200 dark:border-slate-700 max-w-full overflow-visible">
          <h4 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">Error Analysis</h4>
          
          <!-- Error Type Summary -->
          <div class="mb-6">
            <h5 class="text-md font-medium text-slate-800 dark:text-slate-100 mb-3">Error Types Summary</h5>
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div 
                v-for="(count, errorType) in stats.error_breakdown.error_types" 
                :key="errorType" 
                @click="setActiveErrorTab(errorType)"
                class="bg-red-50 p-3 sm:p-4 rounded-lg text-center border border-red-200 cursor-pointer transition-all duration-200 hover:bg-red-100 hover:border-red-300 hover:shadow-md"
                :class="{ 'bg-red-100 border-red-400 shadow-md': activeErrorTab === errorType }"
              >
                <p class="text-sm font-medium text-red-700 capitalize">{{ errorType.replace('_', ' ') }}</p>
                <p class="text-xl font-bold text-red-900">{{ count }}</p>
                <p class="text-xs text-red-600 mt-1">Click to view contacts</p>
              </div>
            </div>
          </div>

          <!-- Active Error Tab Contact List -->
          <div v-if="activeErrorTab && activeErrorContacts.length > 0" class="mb-6">
            <div class="flex items-center justify-between mb-4">
              <h5 class="text-md font-medium text-gray-900 dark:text-slate-100">
                {{ activeErrorTab.replace('_', ' ') }} Error Details ({{ activeErrorContacts.length }} contacts)
              </h5>
              <button 
                @click="setActiveErrorTab(null)"
                class="text-sm text-gray-500 hover:text-gray-700 flex items-center"
              >
                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
                Close
              </button>
            </div>
            <div class="overflow-x-auto -mx-2 sm:mx-0">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider break-words">Contact</th>
                    <th class="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider break-words">Phone / Email</th>
                    <th class="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider break-words">Error Code</th>
                    <th class="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider break-words">Error Message</th>
                    <th class="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider break-words">Time</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="contact in activeErrorContacts" :key="contact.message_id" class="hover:bg-gray-50">
                    <td class="px-2 sm:px-6 py-4 text-sm font-medium text-gray-900 break-words">
                      <span
                        class="text-blue-600 hover:underline cursor-pointer"
                        @click="openContactInfoPanelByPhone(contact.contact_phone, contact.contact_name, contact.contact_email)"
                        :title="'View ' + (contact.contact_name || 'contact') + ' details'"
                      >
                        {{ contact.contact_name || 'Unknown' }}
                      </span>
                    </td>
                    <td class="px-2 sm:px-6 py-4 text-sm text-gray-500 break-words">
                      {{ contactDisplayLine(contact) }}
                    </td>
                    <td class="px-2 sm:px-6 py-4">
                      <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        {{ contact.error_code || 'N/A' }}
                      </span>
                    </td>
                    <td class="px-2 sm:px-6 py-4 text-sm text-gray-500 max-w-xs break-words" :title="contact.error_message">
                      {{ contact.error_message }}
                    </td>
                    <td class="px-2 sm:px-6 py-4 text-sm text-gray-500 break-words">
                      {{ formatEventTime(contact.created_at) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>

        <!-- Reply Contacts Analysis -->
        <div v-if="replyContacts && replyContacts.length > 0" class="bg-white dark:bg-slate-800 rounded-lg shadow-sm border p-6 mt-6 border-slate-200 dark:border-slate-700 max-w-full overflow-visible">
              <div class="flex items-center justify-between mb-4">
                <h4 class="text-lg font-medium text-slate-800 dark:text-slate-100">Reply Contacts</h4>
                <button 
                  @click="toggleReplyContacts"
                  class="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-slate-700 border border-blue-200 dark:border-slate-600 rounded-md hover:bg-blue-100 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-slate-800"
                >
              <svg v-if="!showReplyContacts" class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
              </svg>
              <svg v-else class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"></path>
              </svg>
              {{ showReplyContacts ? 'Hide' : 'Show' }} Reply Contacts ({{ replyContacts.length }})
            </button>
          </div>
          
          <div v-if="showReplyContacts" class="mt-4">
            <div class="overflow-x-auto -mx-2 sm:mx-0">
              <table class="min-w-full divide-y divide-gray-200 dark:divide-slate-600">
                <thead class="bg-gray-50 dark:bg-slate-700">
                  <tr>
                    <th class="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-300 uppercase tracking-wider break-words">Contact</th>
                    <th class="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-300 uppercase tracking-wider break-words">Phone / Email</th>
                    <th class="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-300 uppercase tracking-wider break-words">First Reply Time</th>
                    <th class="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-300 uppercase tracking-wider break-words">Reply Message</th>
                  </tr>
                </thead>
                <tbody class="bg-white dark:bg-transparent divide-y divide-gray-200 dark:divide-slate-700">
                  <tr v-for="contact in replyContacts" :key="contact.contact_id" class="hover:bg-gray-50 dark:hover:bg-slate-700/40">
                    <td class="px-2 sm:px-6 py-4 text-sm font-medium text-gray-900 dark:text-slate-100 break-words">
                      <button 
                      @click="openContactInfoPanelByPhone(contact.contact_phone, contact.contact_name, contact.contact_email)"
                        class="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline cursor-pointer"
                        :title="`View details for ${contact.contact_name || 'Unknown'}`"
                      >
                        {{ contact.contact_name || 'Unknown' }}
                      </button>
                    </td>
                    <td class="px-2 sm:px-6 py-4 text-sm text-gray-500 dark:text-slate-300 break-words">
                      {{ contactDisplayLine(contact) }}
                    </td>
                    <td class="px-2 sm:px-6 py-4 text-sm text-gray-500 dark:text-slate-300 break-words">
                      {{ formatEventTime(contact.first_reply_time) }}
                    </td>
                    <td class="px-2 sm:px-6 py-4 text-sm text-gray-500 dark:text-slate-300 break-words max-w-xs" :title="contact.reply_content">
                      <div class="truncate">
                        {{ contact.reply_content || 'No message content' }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Campaign Timeline -->
        <div v-if="timeline && timeline.length > 0" class="bg-white dark:bg-slate-800 rounded-lg shadow-sm border p-6 mt-6 border-slate-200 dark:border-slate-700">
          <h4 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">Campaign Timeline</h4>
          <div class="space-y-4">
            <div v-for="event in timeline" :key="event.timestamp" class="flex items-start">
              <div class="flex-shrink-0">
                <div class="w-3 h-3 bg-blue-600 rounded-full"></div>
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-gray-900 dark:text-slate-100">{{ event.description }}</p>
                <p class="text-xs text-gray-500 dark:text-slate-400">{{ formatEventTime(event.timestamp) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
          v-show="showContactViewPane"
          class="modal-overlay-v1"
          @click="closeContactInfoPanel"
        >
        </div>

        <contact-info-panel
        v-if="showContactViewPane"
        :contact="Contact"
        :on-close="closeContactInfoPanel"
        :is-modal-view="true"
        @contact-deleted="onContactDeleted"
      />
    </div>

  </template>
  
<script>
  import CampaignsAPI from 'dashboard/api/campaigns';
  import { formatDistanceToNow } from 'date-fns';
  import { formatDurationFromHours } from 'shared/helpers/DateHelper';
  import ContactInfoPanel from 'dashboard/routes/dashboard/contacts/components/ContactInfoPanel.vue';
  
  export default {
    components: {
      ContactInfoPanel,
    },
    data() {
      return {
        campaign: null,
        audience: null,
        stats: null,
        timeline: null,
        engagement: null,
        isLoading: false,
        error: null,
        macro: null,
        campaignInProgress: false,
        campaignState: null,
        retryAttempts: 0,
        campaignProcessing: false,
        activeErrorTab: null,
        showRepliesTooltip: false,
        replyContacts: [],
        showReplyContacts: false,
        showContactViewPane: false,
        Contact: '',
        selectedContactId: '',
        sendgridStats: null,

      };
    },
    computed: {
      audienceLabels() {
        return this.audience?.labels || [];
      },
      audienceFolderName() {
        return this.audience?.folder_name || '';
      },
      audienceType() {
        if (this.audience?.type) return this.audience.type;
        if (this.audience?.name || this.audienceFolderName) return 'segment';
        return 'labels';
      },
      audienceTypeLabel() {
        if (this.audienceType === 'segment') return 'Target Segment';
        if (this.audienceType === 'import') return 'Imported Contacts';
        return 'Target Labels';
      },
      audienceDisplayName() {
        return this.audience?.name || this.audienceFolderName;
      },
      showAudienceLabels() {
        return this.audienceType !== 'segment' && this.audienceLabels.length > 0;
      },
      showAudienceSection() {
        return (
          this.showAudienceLabels ||
          Boolean(this.audienceDisplayName) ||
          this.audienceType === 'segment' ||
          this.audienceType === 'import'
        );
      },
      isCampaignScheduled() {
        return this.campaignState === 'scheduled';
      },
      isCampaignPendingSchedule() {
        return this.campaignState === 'pending_schedule';
      },
      hasTemplateErrors() {
        return this.campaign?.trigger_rules?.has_template_errors === true;
      },
      templateDeletionErrorMessage() {
        return this.campaign?.trigger_rules?.template_deletion_error?.message || 
          'Some email templates used in this campaign are missing or were deleted.';
      },
      deletedTemplates() {
        return this.campaign?.trigger_rules?.template_deletion_error?.deleted_templates || [];
      },
      /**
       * When the campaign uses SendGrid and category stats loaded successfully, overlay
       * delivery/read/failure counts and derived rates so Performance Metrics match SendGrid.
       */
      sendgridPerformanceOverlay() {
        const campaign = this.campaign;
        const sg = this.sendgridStats;
        if (!campaign || campaign.email_outbound_provider !== 'sendgrid') return null;
        if (!sg || sg.error) return null;

        const delivered = Number(sg.delivered) || 0;
        const failed = Number(sg.failed) || 0;
        const uniqueOpens = Number(sg.unique_opens) || 0;
        const requests = Number(sg.requests) || 0;
        const attempted = requests > 0 ? requests : delivered + failed;

        const deliveryRate = attempted > 0
          ? Math.round((delivered / attempted) * 10000) / 100
          : 0;
        const readRate = delivered > 0
          ? Math.round((uniqueOpens / delivered) * 10000) / 100
          : 0;
        const engagementScore = Math.round((deliveryRate * 0.6 + readRate * 0.4) * 100) / 100;

        return {
          delivery_rate: deliveryRate,
          read_rate: readRate,
          engagement_score: engagementScore,
          failed,
          total_messages: attempted,
          sent: attempted,
          delivered,
          read: uniqueOpens,
          unique_recipients: attempted,
        };
      },
      mergedStats() {
        if (!this.stats) return null;
        const overlay = this.sendgridPerformanceOverlay;
        if (!overlay) return this.stats;
        return { ...this.stats, ...overlay };
      },
      errorsByType() {
        if (!this.stats?.error_breakdown?.detailed_errors) return {};
        
        const grouped = {};
        this.stats.error_breakdown.detailed_errors.forEach(error => {
          if (!grouped[error.error_type]) {
            grouped[error.error_type] = [];
          }
          grouped[error.error_type].push(error);
        });
        return grouped;
      },
      activeErrorContacts() {
        if (!this.activeErrorTab || !this.errorsByType[this.activeErrorTab]) return [];
        return this.errorsByType[this.activeErrorTab];
      },
      isCampaignInProgress() {
        return this.campaignState === 'in_progress';
      },
      campaignStatusMessage() {
        if (this.isCampaignScheduled) {
          return 'Campaign Scheduled';
        } else if (this.isCampaignPendingSchedule) {
          return 'Campaign Pending Schedule';
        } else if (this.isCampaignInProgress) {
          return 'Campaign in Progress';
        }
        return 'Campaign Status Unknown';
      },
      campaignStatusDescription() {
        if (this.isCampaignScheduled) {
          const scheduledTime = new Date(this.campaign?.scheduled_at);
          return `Your email campaign "${this.campaign?.title}" is scheduled to start at ${scheduledTime.toLocaleString()}. Delivery reports will be available once the campaign begins.`;
        } else if (this.isCampaignPendingSchedule) {
          return `Your email campaign "${this.campaign?.title}" is created but not yet scheduled. Please set a schedule time to start the campaign.`;
        } else if (this.isCampaignInProgress) {
          return `Your email campaign "${this.campaign?.title}" is currently being processed. Delivery reports will be available once the campaign starts sending messages.`;
        }
        return 'Please check back later for campaign updates.';
      },
      failedDisplay() {
        if (!this.stats) return "--";
        const s = this.mergedStats || this.stats;
        if (this.effectiveStatus === 'completed') {
          const read = Number(s.read) || 0;
          const sent = Number(s.sent) || 0;
          const diff = read - sent;
          return diff >= 0 ? diff : 0;
        }
        return s.failed ?? "--";
      },
      statusClass() {
        switch (this.campaign?.campaign_status) {
          case 'active': return 'bg-green-100 text-green-800';
          case 'draft': return 'bg-gray-100 text-gray-800';
          case 'scheduled': return 'bg-blue-100 text-blue-800';
          case 'failed': return 'bg-red-100 text-red-800';
          case 'completed': return 'bg-green-100 text-green-800';
          default: return 'bg-gray-100 text-gray-800';
        }
      },
      completionStatusClass() {
        switch (this.campaign?.completion_status) {
          case 'highly_successful': return 'bg-green-100 text-green-800';
          case 'successful': return 'bg-blue-100 text-blue-800';
          case 'moderate_success': return 'bg-yellow-100 text-yellow-800';
          case 'needs_attention': return 'bg-red-100 text-red-800';
          case 'completed': return 'bg-green-100 text-green-800';
          default: return 'bg-gray-100 text-gray-800';
        }
      },
      effectiveStatus() {
    if (!this.campaign) return '';
    const scheduled = new Date(this.campaign.scheduled_at);
    const now = new Date();

    // If scheduled_at < now → mark completed
    if (this.campaign.campaign_status === 'active' && scheduled < now) {
      return 'completed';
    }
      return this.campaign.campaign_status;
    },
      campaignId() {
        return this.$route.params.campaigns_id;
      },
      scheduledAtDate() {
        if (!this.campaign?.scheduled_at) return null;
        return new Date(this.campaign.scheduled_at);
      },
      timeRemaining() {
        if (!this.scheduledAtDate) return "--";
        const now = new Date();
        // Show remaining time only if scheduled in future
        if (this.scheduledAtDate > now) {
          return formatDistanceToNow(this.scheduledAtDate, { addSuffix: true });
        }
        return "completed";
      },
      formattedScheduledAt() {
    if (!this.campaign?.scheduled_at) return '';
    return new Date(this.campaign.scheduled_at).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',   
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  },
  formattedCreatedAt() {
    if (!this.campaign?.created_at) return '';
    return new Date(this.campaign.created_at).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  },
      formattedCampaignDuration() {
        return formatDurationFromHours(this.campaign?.campaign_duration);
      },
      campaignDurationLabel() {
        const hours = this.campaign?.campaign_duration;
        if (hours == null || hours < 0) {
          return 'Time left';
        }
        return 'Time elapsed';
      },
    },
    watch: {
      '$route.params.campaigns_id': {
        immediate: true,
        handler(newVal) {
          if (newVal) {
            this.fetchReport();
          }
        },
      },
    },
    methods: {
      contactDisplayLine(contact) {
        const email = contact.contact_email;
        if (email && email !== 'N/A') return email;
        return contact.contact_phone || 'N/A';
      },
      formatSendgridErrorMessage(message) {
        if (message == null) return '';
        if (typeof message === 'string') return message;
        try {
          return JSON.stringify(message);
        } catch (_e) {
          return String(message);
        }
      },
      onContactDeleted() {
        this.selectedContactId = '';
        this.showContactViewPane = false;
        this.fetchReport();
      },
      hideRepliesTooltip() {
        // Add a small delay to prevent flickering when moving between icon and tooltip
        setTimeout(() => {
          this.showRepliesTooltip = false;
        }, 100);
      },
      toggleReplyContacts() {
        this.showReplyContacts = !this.showReplyContacts;
      },
      openContactModal(contactId) {
        // Open contact details page in new tab
        const accountId = this.$route.params.accountId;
        const contactUrl = `/app/accounts/${accountId}/contacts/${contactId}`;
        window.open(contactUrl, '_blank');
      },
      async fetchReport() {
        this.isLoading = true;
        this.error = null;
        this.campaignProcessing = false;
        try {
          const { data } = await CampaignsAPI.getEmailCampaignReport(this.campaignId);

          this.campaign = data.campaign;
          this.audience = data.audience;
          this.stats = data.stats;
          this.macro = data.macro;
          this.timeline = data.timeline;
          this.engagement = data.engagement;
          this.replyContacts = data.reply_contacts || [];
          this.sendgridStats = data.sendgrid_stats || null;
          this.campaignInProgress = data.campaign_in_progress || false;
          this.campaignState = data.campaign_state || null;
          this.retryAttempts = 0; // Reset retry attempts on success
          this.campaignProcessing = false; // Reset processing state on success
        } catch (e) {
          const errorMessage = e?.response?.data?.error || e.message || 'Failed to load';
          const isCampaignNotFound = e?.response?.data?.campaign_not_found;
          
          // Check if campaign is not found (newly created campaign not yet available)
          if (e?.response?.status === 404 && isCampaignNotFound) {
            this.retryAttempts += 1;
            
            if (this.retryAttempts < 5) {
              // Don't show error, just keep loading and retry silently
              this.error = null;
              this.campaignInProgress = false;
              this.campaignState = null;
              this.campaignProcessing = true;
              
              // Auto-retry after 1 second for newly created campaigns
              setTimeout(() => {
                if (this.campaignId) {
                  this.fetchReport();
                }
              }, 1000);
            } else {
              // Only show error after 5 attempts
              this.error = "Campaign is taking longer than expected to process. Please refresh the page.";
              this.campaignInProgress = false;
              this.campaignState = null;
            }
          }
          // Check if campaign is in progress (404 error means no messages sent yet)
          else if (e?.response?.status === 404 && errorMessage.includes('Resource could not be found')) {
            this.campaignInProgress = true;
            this.error = null;
          } else {
            this.error = errorMessage;
            this.campaignInProgress = false;
            this.campaignState = null;
          }
        } finally {
          this.isLoading = false;
        }
      },
      async openContactInfoPanelByPhone(phoneNumber, fallbackName = '', email = '') {
        try {
          if (!phoneNumber && !email) return;
          const accountId = this.$route.params.accountId;
          const { data } = await axios.get(`/api/v1/accounts/${accountId}/contacts/check_contact_availability`, {
            params: {
              phone_number: phoneNumber || undefined,
              email: email || undefined,
            }
          });

          if (data && data.success && Array.isArray(data.contact_id) && data.contact_id.length > 0) {
            const contact = data.contact_id[0];
            this.Contact = contact;
            this.selectedContactId = contact.id;
            this.showContactViewPane = true;
          } else {
            // Fallback minimal object if not found
            this.Contact = { id: null, name: fallbackName, phone_number: phoneNumber, email };
            this.selectedContactId = '';
            this.showContactViewPane = false;
          }
        } catch (e) {
          // Still open with minimal info so user sees something
          this.Contact = { id: null, name: fallbackName, phone_number: phoneNumber, email };
          this.selectedContactId = '';
          this.showContactViewPane = false;
        }
      },
      formatEventTime(timestamp) {
        if (!timestamp) return '';
        return new Date(timestamp).toLocaleString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      },
      setActiveErrorTab(errorType) {
        this.activeErrorTab = this.activeErrorTab === errorType ? null : errorType;
      },
      goBackToCampaigns() {
        this.$router.push({
          name: 'email_campaigns',
          params: { accountId: this.$route.params.accountId }
        });
      },
      closeContactInfoPanel() {
        this.selectedContactId = '';
        this.showContactViewPane = false;
        this.showContactInfoPanelPane = false;
      },
    },
  };
  </script>



  <style scoped>
  .modal-overlay-v1 {
    position: fixed;
    left: 0;
    top: 0;
    z-index: 39;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.4);
    width: 100%;
    height: 100%;
  }

  .custom-spinner {
    position: relative;
    display: inline-block;
    width: 32px;
    height: 32px;
    vertical-align: middle;
  }

  .custom-spinner::before {
    content: '';
    box-sizing: border-box;
    position: absolute;
    top: 50%;
    left: 50%;
    width: 32px;
    height: 32px;
    margin-top: -16px;
    margin-left: -16px;
    border-radius: 50%;
    border: 3px solid rgba(64, 81, 138, 0.3);
    border-top-color: #40518A;
    animation: custom-spinner-rotate 0.9s linear infinite;
  }

  @keyframes custom-spinner-rotate {
    to {
      transform: rotate(360deg);
    }
  }

  .back-to-campaigns-btn,
  .refresh-report-btn,
  .refresh-status-btn {
    background-color: #40518A !important;
    color: white !important;
    border: 1px solid #40518A !important;
    padding: 10px !important;
    line-height: 1.2 !important;
  }
  
  .back-to-campaigns-btn:hover,
  .refresh-report-btn:hover,
  .refresh-status-btn:hover {
    background-color: #344170 !important;
  }
  
  .back-to-campaigns-btn svg,
  .refresh-report-btn svg,
  .refresh-status-btn svg {
    color: white !important;
    stroke: white !important;
  }
  </style>
    
  

   